/**
 * Privacy Policy Page
 * World-class privacy policy with GDPR compliance
 * Public legal page accessible without authentication
 */

import LegalPageLayout from '@/components/legal/LegalPageLayout';
import { LEGAL_LAST_UPDATED } from '@/constants/legalVersions';

export default function PrivacyPage() {
  return (
    <LegalPageLayout 
      title="Privacy Policy" 
      lastUpdated={LEGAL_LAST_UPDATED.privacy}
      metaDescription="Privacy Policy for FinList - Learn how we collect, use, store, and protect your personal data in compliance with UK GDPR and EU GDPR."
    >
      {/* Section 1: Introduction */}
      <section>
        <h2>1. Introduction</h2>
        <p>
          FinList ("FinList", "we", "our", or "us") is committed to protecting your privacy.
          This Privacy Policy explains how we collect, use, store, disclose, and safeguard your 
          personal data when you use the FinList application and related services (the "Service").
        </p>
        <p>This Policy is designed to comply with:</p>
        <ul>
          <li><strong>UK General Data Protection Regulation (UK GDPR)</strong></li>
          <li><strong>EU General Data Protection Regulation (EU GDPR)</strong>, where applicable</li>
        </ul>
        <p>
          By using FinList, you acknowledge that you have read and understood this Privacy Policy.
        </p>
      </section>

      {/* Section 2: Information We Collect */}
      <section>
        <h2>2. Information We Collect</h2>
        <p>We collect the following categories of information:</p>
        
        <h3>2.1 Information You Provide Directly</h3>
        <ul>
          <li>Name and email address</li>
          <li>Account login credentials</li>
          <li>Budget envelopes, transactions, shopping lists, tasks, and goals</li>
          <li>Preferences and settings you choose within FinList</li>
        </ul>

        <h3>2.2 Information Collected Automatically</h3>
        <ul>
          <li>Device and browser information</li>
          <li>Usage data (pages visited, features used, interactions)</li>
          <li>Log files, diagnostics, and performance data</li>
        </ul>
      </section>

      {/* Section 3: How We Use Your Information */}
      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use your personal data to:</p>
        <ul>
          <li>Provide, operate, and maintain the FinList Service</li>
          <li>Personalize your experience and improve usability</li>
          <li>Generate insights, summaries, and analytics</li>
          <li>Improve product features and performance</li>
          <li>Communicate service updates, security alerts, and important notices</li>
          <li>Detect, prevent, and address fraud or security issues</li>
        </ul>
      </section>

      {/* Section 4: Legal Basis for Processing */}
      <section>
        <h2>4. Legal Basis for Processing (GDPR)</h2>
        <p>Under GDPR, we process personal data on the following legal bases:</p>
        <ul>
          <li><strong>Contractual necessity</strong> – to provide the Service you request</li>
          <li><strong>Consent</strong> – where you have explicitly agreed</li>
          <li><strong>Legitimate interests</strong> – to improve and secure the Service</li>
          <li><strong>Legal obligations</strong> – where required by law</li>
        </ul>
      </section>

      {/* Section 5: AI & Automated Data Processing */}
      <section>
        <h2>5. AI &amp; Automated Data Processing</h2>
        <p>FinList uses AI-powered systems to provide insights, recommendations, and summaries.</p>
        <ul>
          <li>AI processing is limited to what is necessary to deliver features</li>
          <li>We minimize data shared with AI providers</li>
          <li>AI providers are contractually restricted from using your data for training or secondary purposes beyond service delivery</li>
        </ul>
      </section>

      {/* Section 6: Data Sharing and Disclosure */}
      <section>
        <h2>6. Data Sharing and Disclosure</h2>
        <p><strong>We do not sell your personal data.</strong></p>
        <p>We may share data with trusted third parties, including:</p>
        <ul>
          <li>Cloud infrastructure providers (e.g., Firebase)</li>
          <li>Payment processors (e.g., Stripe)</li>
          <li>Analytics providers, using anonymized or aggregated data where possible</li>
        </ul>
        <p>
          All third parties are contractually obligated to protect your data and use it only for authorized purposes.
        </p>
      </section>

      {/* Section 7: Data Retention */}
      <section>
        <h2>7. Data Retention</h2>
        <p>We retain personal data only for as long as necessary to:</p>
        <ul>
          <li>Provide the Service</li>
          <li>Fulfil legal, regulatory, or contractual obligations</li>
        </ul>
        <p>You may request deletion of your account and associated data at any time.</p>
      </section>

      {/* Section 8: Data Security */}
      <section>
        <h2>8. Data Security</h2>
        <p>We apply industry-standard security measures, including:</p>
        <ul>
          <li>Encryption of data in transit and at rest</li>
          <li>Role-based access controls</li>
          <li>Secure authentication mechanisms</li>
        </ul>
        <p>
          While we take reasonable steps to protect your data, no system can be guaranteed to be 100% secure.
        </p>
      </section>

      {/* Section 9: Your Data Protection Rights */}
      <section>
        <h2>9. Your Data Protection Rights</h2>
        <p>Depending on your location, you have the right to:</p>
        <ul>
          <li>Access your personal data</li>
          <li>Correct inaccurate or incomplete data</li>
          <li>Request deletion of your data</li>
          <li>Restrict or object to processing</li>
          <li>Request data portability</li>
        </ul>
        <p>
          Requests can be submitted to:{' '}
          <a href="mailto:support@getfinlist.com" className="font-medium">
            📧 support@getfinlist.com
          </a>
        </p>
      </section>

      {/* Section 10: Cookies and Tracking Technologies */}
      <section>
        <h2>10. Cookies and Tracking Technologies</h2>
        <p>FinList may use cookies and similar technologies to:</p>
        <ul>
          <li>Maintain user sessions</li>
          <li>Improve performance and reliability</li>
          <li>Understand how users interact with the Service</li>
        </ul>
        <p>You can control cookies through your browser settings.</p>
      </section>

      {/* Section 11: International Data Transfers */}
      <section>
        <h2>11. International Data Transfers</h2>
        <p>
          If your data is transferred outside the UK or EU, we ensure appropriate safeguards are in place, such as:
        </p>
        <ul>
          <li>Standard Contractual Clauses (SCCs)</li>
          <li>Equivalent legal protections</li>
        </ul>
      </section>

      {/* Section 12: Children's Privacy */}
      <section>
        <h2>12. Children's Privacy</h2>
        <p>
          FinList is not intended for individuals under the age of 18.
          We do not knowingly collect personal data from children.
        </p>
      </section>

      {/* Section 13: Changes to This Privacy Policy */}
      <section>
        <h2>13. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time.
          Material changes will be communicated through the Service or by other appropriate means.
        </p>
      </section>

      {/* Section 14: Contact Us */}
      <section>
        <h2>14. Contact Us</h2>
        <p>For privacy-related questions or concerns, contact us at:</p>
        <p className="text-lg">
          <a href="mailto:privacy@getfinlist.com" className="font-medium">
            📧 privacy@getfinlist.com
          </a>
        </p>
      </section>
    </LegalPageLayout>
  );
}
